import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import sessionApis from '../../../api/services/session.service';
const initialState: any = {
  value: {
    targets: <any>[],
    targetData: <any>[],
    programBookUUID: '',
    data: {
      id: '',
      name: '',
      clientId: {
        id: '',
      },
    },
  },
  // Form state to persist user input during component re-renders
  formState: {
    sessionName: '',
    createdFor: [],
  },
  latestRequestedRunId: '',
  allTargets: <any>{},
  selectedSummary: {},
  error: false,
  loading: false,
  selectedRow: 0,
  sessionHistory: [],
};
interface GetSessionData {
  sessionId: string;
}
interface GetSessionRunId {
  sessionRunId: string;
}
interface HistoryData {
  sessionId: any;
}
export const getSession = createAsyncThunk(
  'getSessionData',
  async (payload: GetSessionData, { rejectWithValue }) => {
    try {
      const res = await sessionApis.getSessionById(payload);
      return {
        ...res.data.data,
      };
    } catch (err) {
      rejectWithValue('Error in Response');
    }
  }
);
export const getTargetRunSummary = createAsyncThunk(
  'getTargetRunSummary',
  async (payload: GetSessionRunId, { rejectWithValue }) => {
    try {
      const res = await sessionApis.getTargetRunSummary(payload);
      return res.data.data;
    } catch (err) {
      rejectWithValue('Error in Response');
    }
  }
);
export const getAllTargetBySessionId = createAsyncThunk(
  'getSessionTargetsData',
  async (payload: GetSessionData, { rejectWithValue }) => {
    try {
      const res = await sessionApis.getAllTargetsBySession(payload);
      return res.data.data;
    } catch (err) {
      rejectWithValue('Error in Response');
    }
  }
);
export const getHistoryById = createAsyncThunk(
  'getHistoryById',
  async (payload: HistoryData, { rejectWithValue }) => {
    try {
      const res = await sessionApis.sessionHistory(payload);
      return res.data.data;
    } catch (err) {
      rejectWithValue('Error in Response');
    }
  }
);
const sessionSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setSessionData: (state, action) => {
      state.value = { ...action.payload };
    },
    removeTargetById: (state, action) => {
      state.value.targets = state.value.targets.filter(
        ({ id, targetId }: { id: any; targetId?: any }) =>
          targetId ? targetId?.id !== action.payload : id !== action.payload
      );
    },
    removeAllTargets: state => {
      state.value.targets = [];
    },
    reInitSessionData: state => {
      state.value = {
        targets: [],
        programBookUUID: '',
        data: {},
        targetData: [],
      };
      // Clear form state when reinitializing session
      state.formState = {
        sessionName: '',
        createdFor: [],
      };
    },
    // Form state management to persist user input during component re-renders
    setFormSessionName: (state, action) => {
      state.formState.sessionName = action.payload;
    },
    setFormCreatedFor: (state, action) => {
      state.formState.createdFor = action.payload;
    },
    clearFormState: state => {
      state.formState = {
        sessionName: '',
        createdFor: [],
      };
    },
    addTargetToSession: (state, action) => {
      state.value.programBookUUID = action.payload.programBookUUID;
      if (!state.value.targets.find((item: any) => item.id === action.payload.target.id)) {
        const targets = [
          ...state.value.targets,
          {
            ...action.payload.target,
            orderCount: state.value.targets.length,
          },
        ];
        state.value.targets = [...targets];
      }
    },
    selectedSessionSummary: (state, action) => {
      const payload: any = {};
      if (action.payload?.update) {
        state.selectedSummary = action?.payload?.updatedTargetBody;
      } else {
        for (const [key, value] of Object.entries(action?.payload?.sendDataForTarget)) {
          payload[key] = action?.payload?.allTargets[action?.payload?.sendDataForTarget[key]]
            ? action?.payload?.allTargets[action?.payload?.sendDataForTarget[key]]
            : [{ sessionRunId: value }];
        }
        state.selectedSummary = {
          ...action?.payload?.selectedSummary,
          [state.selectedRow]: {
            ...action?.payload?.selectedSummary[state.selectedRow],
            ...payload,
          },
        };
      }
    },
    setSelectedRow: (state, action) => {
      state.selectedRow = action.payload;
    },
    clearRowData: state => {
      state.selectedSummary = {};
      state.selectedRow = 0;
    },
    clearOnlyRow: state => {
      state.selectedRow = 0;
    },
    setTargetDataBySessionRunId: (state, action) => {
      const runId = action.payload;
      state.latestRequestedRunId = runId;
      if (runId && state.allTargets?.[runId]) {
        state.value.targetData = state.allTargets[runId];
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getSession.pending, state => {
        state.loading = true;
      })
      .addCase(getSession.fulfilled, (state: any, action) => {
        state.value.data = action.payload;
        state.loading = false;
      })
      .addCase(getSession.rejected, state => {
        state.error = true;
        state.loading = false;
      })
      .addCase(getAllTargetBySessionId.pending, state => {
        state.loading = true;
      })
      .addCase(getAllTargetBySessionId.fulfilled, (state: any, action) => {
        state.value.targets = action.payload;
        state.loading = false;
      })
      .addCase(getTargetRunSummary.pending, (state: any, action) => {
        const requestedId = action?.meta?.arg?.sessionRunId;
        if (requestedId) {
          state.latestRequestedRunId = requestedId;
        }
        state.loading = true;
      })
      .addCase(getTargetRunSummary.fulfilled, (state: any, action) => {
        const requestedId = action?.meta?.arg?.sessionRunId;
        const targetRunId = requestedId || action?.payload?.[0]?.sessionRunId;
        const payloadData = action?.payload || [];

        if (targetRunId) {
          state.allTargets = {
            ...state?.allTargets,
            [targetRunId]: payloadData,
          };
        }

        if (!requestedId || requestedId === state.latestRequestedRunId) {
          state.value.targetData = payloadData;
        }

        state.loading = false;
      })
      .addCase(getHistoryById.pending, state => {
        state.loading = true;
      })
      .addCase(getHistoryById.fulfilled, (state: any, action) => {
        state.sessionHistory = action.payload;
        state.loading = false;
      })
      .addCase(getHistoryById.rejected, state => {
        state.error = true;
        state.loading = false;
      });
  },
});
export const {
  setSessionData,
  addTargetToSession,
  removeTargetById,
  removeAllTargets,
  reInitSessionData,
  selectedSessionSummary,
  setSelectedRow,
  clearRowData,
  clearOnlyRow,
  setFormSessionName,
  setFormCreatedFor,
  clearFormState,
  setTargetDataBySessionRunId,
} = sessionSlice.actions;
export default sessionSlice.reducer;
